import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useChainlocks } from '../hooks/useChainlocks';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatTimestamp, formatTimeAgo, formatNumber } from '../util/format';
import { decodeSignersBitmap } from '../util/chainlock-decoder';
import type { ChainLock } from '../api/types';

export const ChainLocksPage = () => {
  const { data, loading, error, refetch, pollInterval, pollTick } = useChainlocks();
  const [selectedChainlock, setSelectedChainlock] = useState<ChainLock | null>(null);

  if (loading && !data) return <LoadingSpinner message="Loading ChainLock status..." />;
  if (error) return <ErrorMessage error={error} retry={refetch} />;
  if (!data) return <div>No ChainLock data available</div>;

  const activeFrontierHeight = data.active_chainlock?.block_height ?? null;
  const activeConfirmations =
    data.lag_blocks != null ? data.lag_blocks + 1 : null;

  const getLagStatusInfo = (lagBlocks: number | null) => {
    if (lagBlocks === null) {
      return { label: 'Unknown', className: 'status-unknown' };
    }

    if (lagBlocks === 0) {
      return { label: '⚡ Synced', className: 'status-synced' };
    } else if (lagBlocks <= 10) {
      return { label: '✅ Normal', className: 'status-normal' };
    } else if (lagBlocks <= 20) {
      return { label: '🐢 Slow', className: 'status-slow' };
    } else {
      return { label: '⚠️ Stalled', className: 'status-stalled' };
    }
  };

  const lagStatusInfo = getLagStatusInfo(data.lag_blocks);

  return (
    <div className="chainlocks-page">
      <div className="chainlock-loader">
        <div
          key={pollTick}
          className="chainlock-loader-bar"
          style={{
            animationDuration: `${pollInterval}ms`,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>ChainLocks Monitoring</h1>
          <p className="subtitle" style={{ marginTop: '4px' }}>
            Real-time status of Syscoin ChainLock finality system
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <a
            href="/quorums"
            className="nav-button"
          >
            View Quorums →
          </a>
        </div>
      </div>

      {/* Lag & Frontier Metrics */}
      <section className="metrics-row">
        <div className="metric-card">
          <h3>Frontier</h3>
          <div className="metric-value">
            {activeFrontierHeight != null ? formatNumber(activeFrontierHeight) : 'N/A'}
          </div>
          <div className="metric-label">
            Chain tip: {data.tip_height != null ? formatNumber(data.tip_height) : 'N/A'}
          </div>
        </div>

        <div className="metric-card">
          <h3>Lag Status</h3>
          <div className={`metric-value ${lagStatusInfo.className}`}>
            {lagStatusInfo.label}
          </div>
          <div className="metric-label">
            {data.lag_blocks !== null ? `${data.lag_blocks} block${data.lag_blocks === 1 ? '' : 's'} behind` : 'Unknown'}
          </div>
        </div>

        <div className="metric-card">
          {data.active_chainlock ? (
            <>
              <h3 style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span>UTXO Block #{formatNumber(data.active_chainlock.block_height || 0)}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#8b92a8' }}>
                  {activeConfirmations != null
                    ? `${formatNumber(activeConfirmations)} confirmation${activeConfirmations === 1 ? '' : 's'}`
                    : '0 confirmations'}
                </span>
              </h3>
              <div className="metric-value" style={{ fontSize: '0.85rem', padding: '0.5rem 0' }}>
                <Link
                  to={`/utxo/block/${data.active_chainlock.block_hash}`}
                  style={{ textDecoration: 'none' }}
                >
                  <code className="hash-display hash-link" style={{ fontSize: '0.85rem' }}>
                    {data.active_chainlock.block_hash}
                  </code>
                </Link>
              </div>
            </>
          ) : (
            <>
              <h3>UTXO Block</h3>
              <div className="metric-value">N/A</div>
              <div className="metric-label">No active ChainLock</div>
            </>
          )}
        </div>
      </section>

      {/* Active ChainLock Signature */}
      {data.active_chainlock && (
        <div className="info-grid">
          <div className="info-item">
            <strong>Signature:</strong>
            <code className="hash-display">
              {data.active_chainlock.signature}
            </code>
          </div>
        </div>
      )}

      {/* Active ChainLock Signature Details */}
      {!!data.raw_rpc_output && (
        <>
          <div className="navigation-card">
            {(() => {
              const activeChainlock = data.raw_rpc_output as any;
              const quorumSigners = activeChainlock.signers || '';
              const shares = activeChainlock.shares || [];
              const quorumCount = data.quorum_info?.quorum_count || 4;
              const signerIndices = decodeSignersBitmap(quorumSigners);
              const quorumSignerCount = signerIndices.length;
              const quorumPercentage = quorumCount > 0 ? Math.round((quorumSignerCount / quorumCount) * 100) : 0;
              const thresholdMet = quorumPercentage >= 60;

              // Determine which positions voted and which are from shares
              const shareQuorumPositions = new Set(shares.map((s: any) => decodeSignersBitmap(s.signers)[0]));

              return (
                <div>
                  {/* Header Row: Signature Composition, Quorum Participation, Status */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Signature Composition:</div>
                      <div className="chainlock-detail-value">
                        {shares.length} individual share(s)
                        {quorumSignerCount > shares.length && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: 'var(--color-warning)' }}>
                            + {quorumSignerCount - shares.length} aggregated
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Quorum Participation:</div>
                      <div className="chainlock-detail-value">
                        {quorumSignerCount} of {quorumCount} quorums ({quorumPercentage}%)
                        {thresholdMet && <span style={{ color: 'var(--color-success)', marginLeft: '0.5rem', fontSize: '0.875rem' }}>≥ 60% threshold</span>}
                      </div>
                    </div>
                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Status:</div>
                      <div className="chainlock-detail-value">
                        {thresholdMet ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background: 'rgba(76, 175, 80, 0.2)',
                            color: '#66ff66',
                            border: '1px solid #4CAF50',
                            fontSize: '0.875rem'
                          }}>🔒 LOCKED</span>
                        ) : (
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background: 'rgba(139, 146, 168, 0.2)',
                            color: '#8b92a8',
                            border: '1px solid #4a5568',
                            fontSize: '0.875rem'
                          }}>⚠️ PENDING</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Votes */}
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text-primary)' }}>
                      Votes:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                      {[0, 1, 2, 3].map(pos => {
                        const voted = signerIndices.includes(pos);
                        const hasShare = shareQuorumPositions.has(pos);
                        const source = voted ? (hasShare ? 'Individual' : 'Aggregated') : 'None';

                        return (
                          <div key={pos} style={{ padding: '0.5rem', background: voted ? 'rgba(0, 255, 0, 0.05)' : 'rgba(128, 128, 128, 0.05)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>
                                <strong>Quorum {pos}:</strong> {voted ? '✓ Voted' : '✗ Did not vote'}
                              </span>
                              {voted && (
                                <span className={hasShare ? 'badge-individual' : 'badge-aggregated'}>
                                  {source}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Signature Composition Explanation */}
                  <div style={{ padding: '1rem', borderRadius: '4px', borderLeft: '3px solid var(--color-primary)' }}>
                    <div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: 'var(--color-text-primary)' }}>
                      <strong>ℹ️ Multi-Quorum ChainLock Signatures:</strong>
                    </div>
                    <ul style={{ fontSize: '0.875rem', lineHeight: '1.6', color: 'var(--color-text-secondary)', marginTop: '0.5rem', marginLeft: '1.25rem' }}>
                      <li>4 active quorums, each with 400 sentrynodes · Requires <strong>≥3 quorums (60%)</strong> to sign</li>
                      <li><strong>Individual:</strong> separate quorum signature · <strong>Aggregated:</strong> multiple signatures combined via BLS cryptography</li>
                    </ul>
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      )}

      {/* Recent ChainLocks History */}
      <h2>Recent ChainLocks History ({data.recent_chainlocks.length - (data.active_chainlock ? 1 : 0)})</h2>
      <section className="chainlocks-history">
        {data.recent_chainlocks.length > 0 ? (
          <div className="chainlocks-grid">
            {data.recent_chainlocks
              .filter((cl) => !cl.is_active)
              .map((cl: ChainLock, index: number) => {
              // Calculate block gap from previous chainlock
              let gapEmoji = '';
              const historical = data.recent_chainlocks.filter((c) => !c.is_active);
              if (index < historical.length - 1) {
                const prevCl = historical[index + 1];
                const gap = cl.block_height - prevCl.block_height;

                if (gap < 5) {
                  gapEmoji = ' ⚡'; // Fast (earlier than expected)
                } else if (gap <= 7) {
                  gapEmoji = ''; // Normal (5-7 blocks) - no emoji
                } else if (gap <= 15) {
                  gapEmoji = ' ⏱️'; // Delayed (8-15 blocks)
                } else {
                  gapEmoji = ' 🐢'; // Slow (>15 blocks)
                }
              }

              return (
                <button
                  key={cl.signature}
                  className={`chainlock-box ${cl.is_active ? 'active' : ''}`}
                  onClick={() => setSelectedChainlock(cl)}
                >
                  <div className="chainlock-box-height">
                    {formatNumber(cl.block_height)}
                  </div>
                  {gapEmoji && <div className="chainlock-box-emoji">{gapEmoji.trim()}</div>}
                  <div className="chainlock-box-time">
                    {formatTimeAgo(Math.floor(cl.created_at / 1000))}
                  </div>
                  <div className="chainlock-box-status">
                    {cl.is_active ? (
                      <span className="badge badge-active">Active</span>
                    ) : (
                      <span className="badge badge-inactive">Historical</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="no-data">No ChainLock history available</div>
        )}
      </section>

      {/* ChainLock Details Overlay */}
      {selectedChainlock && (
        <div className="chainlock-overlay" onClick={() => setSelectedChainlock(null)}>
          <div className="chainlock-overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="chainlock-overlay-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                ChainLock Details
                {selectedChainlock.is_active ? (
                  <span className="badge badge-active">Active</span>
                ) : (
                  <span className="badge badge-inactive">Historical</span>
                )}
              </h2>
              <button
                className="chainlock-overlay-close"
                onClick={() => setSelectedChainlock(null)}
              >
                ✕
              </button>
            </div>
            <div className="chainlock-overlay-body">
              {/* Signature - Full Width Row */}
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <strong>Signature:</strong>
                <code className="hash-display" style={{ marginBottom: '12px' }}>
                  {selectedChainlock.signature}
                </code>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: '1 1 250px' }}>
                    <div style={{ color: '#8b92a8', marginBottom: '4px' }}>First Seen:</div>
                    <div>
                      {formatTimestamp(Math.floor(selectedChainlock.created_at / 1000))}
                      {' '}({formatTimeAgo(Math.floor(selectedChainlock.created_at / 1000))})
                    </div>
                  </div>
                  {selectedChainlock.updated_at && selectedChainlock.updated_at !== selectedChainlock.created_at && (
                    <div style={{ flex: '1 1 250px' }}>
                      <div style={{ color: '#8b92a8', marginBottom: '4px' }}>Last Seen:</div>
                      <div>
                        {formatTimestamp(Math.floor(selectedChainlock.updated_at / 1000))}
                        {' '}({formatTimeAgo(Math.floor(selectedChainlock.updated_at / 1000))})
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* UTXO Block */}
              <div className="info-item">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' }}>
                  <strong>
                    UTXO Block #{formatNumber(selectedChainlock.block_height)}
                  </strong>
                  <span style={{ fontSize: '0.85em', color: '#8b92a8', whiteSpace: 'nowrap' }}>
                    ({formatNumber(
                      data.tip_height != null && selectedChainlock.block_height != null
                        ? data.tip_height - selectedChainlock.block_height + 1
                        : 0
                    )} confirmation{
                      (data.tip_height != null && selectedChainlock.block_height != null
                        ? data.tip_height - selectedChainlock.block_height + 1
                        : 0) === 1 ? '' : 's'
                    })
                  </span>
                </div>
                <div style={{ marginTop: '6px' }}>
                  <Link
                    to={`/utxo/block/${selectedChainlock.block_hash}`}
                    style={{ textDecoration: 'none', display: 'inline-block' }}
                  >
                    <code className="hash-display hash-link">
                      {selectedChainlock.block_hash}
                    </code>
                  </Link>
                </div>
              </div>

              {/* Quorum Participation */}
              {selectedChainlock.decoded_votes && (
                <div className="info-item">
                  <strong>Quorum Participation:</strong>
                  <div style={{ marginTop: '6px', marginBottom: '8px' }}>
                    {selectedChainlock.decoded_votes.threshold_met ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: 'rgba(76, 175, 80, 0.2)',
                        color: '#66ff66',
                        border: '1px solid #4CAF50',
                        fontSize: '0.875rem'
                      }}>🔒 LOCKED</span>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: 'rgba(139, 146, 168, 0.2)',
                        color: '#8b92a8',
                        border: '1px solid #4a5568',
                        fontSize: '0.875rem'
                      }}>⚠️ PENDING</span>
                    )}
                  </div>
                  <div>
                    {selectedChainlock.decoded_votes.voted_count} of {selectedChainlock.decoded_votes.total_quorums} quorums ({selectedChainlock.decoded_votes.vote_percentage?.toFixed(1)}%)
                    {selectedChainlock.decoded_votes.threshold_met && (
                      <span style={{ color: 'var(--color-success)', marginLeft: '8px' }}>
                        ≥ 60% threshold
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Vote breakdown section */}
              {selectedChainlock.decoded_votes && (
                <div className="chainlock-votes-table-container" style={{ gridColumn: '1 / -1' }}>
                  <h4>Detailed Vote Breakdown</h4>
                  <table className="chainlock-votes-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                      <col style={{ width: '45px' }} />
                      <col style={{ width: 'auto', minWidth: '0' }} />
                      <col className="height-column" style={{ width: '100px' }} />
                      <col style={{ width: '55px' }} />
                      <col style={{ width: '120px' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Pos</th>
                        <th>Quorum Hash</th>
                        <th className="height-column">Height</th>
                        <th>Voted</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedChainlock.decoded_votes.votes.map(vote => (
                        <tr key={vote.position} className={vote.voted ? 'voted' : 'not-voted'}>
                          <td>{vote.position}</td>
                          <td style={{ maxWidth: 0 }}>
                            <code style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {vote.quorum_hash}
                            </code>
                          </td>
                          <td className="height-column">{vote.quorum_height !== null ? formatNumber(vote.quorum_height) : 'N/A'}</td>
                          <td>{vote.voted ? '✓' : '✗'}</td>
                          <td>
                            {vote.source === 'individual' && <span className="badge-individual">Individual</span>}
                            {vote.source === 'aggregated' && <span className="badge-aggregated">Aggregated</span>}
                            {vote.source === 'none' && <span className="badge-none">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Signature Composition */}
              {selectedChainlock.decoded_votes && (
                <div style={{
                  gridColumn: '1 / -1',
                  padding: '0.75rem 1rem',
                  background: 'rgba(74, 158, 255, 0.1)',
                  borderLeft: '3px solid var(--color-primary)',
                  borderRadius: '4px'
                }}>
                  <strong>Signature Composition:</strong>{' '}
                  {selectedChainlock.decoded_votes.votes.filter((v: any) => v.source === 'individual').length || 0} individual share{(selectedChainlock.decoded_votes.votes.filter((v: any) => v.source === 'individual').length || 0) === 1 ? '' : 's'}
                  {(selectedChainlock.decoded_votes.votes.filter((v: any) => v.source === 'aggregated').length || 0) > 0 && (
                    <span style={{ color: 'var(--color-warning)' }}>
                      {' '}+ {selectedChainlock.decoded_votes.votes.filter((v: any) => v.source === 'aggregated').length || 0} aggregated
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Raw RPC Output */}
      {data.raw_rpc_output ? (
        <>
          <h2>Raw RPC Output (getchainlocks)</h2>
          {data.raw_rpc_updated_at && (
            <p className="subtitle">
              {formatTimestamp(Math.floor(data.raw_rpc_updated_at / 1000))}
              {' '}({formatTimeAgo(Math.floor(data.raw_rpc_updated_at / 1000))})
            </p>
          )}
          <section className="raw-rpc-section">
            <textarea
              className="raw-rpc-output"
              readOnly
              value={JSON.stringify(data.raw_rpc_output, null, 2)}
              rows={15}
            />
          </section>
        </>
      ) : null}

      {/* Documentation */}
      <h2 className="chainlocks-info-heading">ℹ️ About ChainLocks</h2>
      <div className="navigation-card">
        <p style={{ marginBottom: '10px' }}>
          <strong>ChainLocks</strong> provide instant finality for Syscoin blocks through
          multi-quorum consensus (MQCL). All blocks at or below the frontier height (H_CL)
          are considered final and protected from reorganizations.
        </p>
        <p style={{ marginBottom: '10px' }}>
          The ChainLocks page summarizes the current frontier, lag relative to the UTXO tip,
          and a short history of recent ChainLock signatures observed by the indexer.
        </p>
        <ul
          style={{
            marginLeft: '20px',
            marginBottom: '15px',
            color: '#c0c6d4',
          }}
        >
          <li>
            <strong>Frontier Model:</strong> One ChainLock signature covers all blocks ≤ H_CL
          </li>
          <li>
            <strong>Synced (⚡):</strong> 0 blocks behind (perfect sync)
          </li>
          <li>
            <strong>Normal (✅):</strong> 1–10 blocks (expected operation)
          </li>
          <li>
            <strong>Slow (🐢):</strong> 11–20 blocks (degraded performance)
          </li>
          <li>
            <strong>Stalled (⚠️):</strong> &gt;20 blocks (requires investigation)
          </li>
        </ul>
      </div>
    </div>
  );
};
